import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Image as ImageIcon, MessageCircle } from "lucide-react";
import Modal from "./Modal";
import { useI18n } from "../i18n";

const MAIL_TO = "info@treviagroup.com";
const WHATSAPP = "https://wa.me/38631708897";

export default function Footer() {
  const { t } = useI18n();
  const [showWechat, setShowWechat] = useState(false);
  const [showCard, setShowCard] = useState(false);

  return (
    <footer className="bg-navy text-white">
      {/* Gradient accent line */}
      <div className="h-[2px] w-full bg-gradient-to-r from-teal via-cyan to-gold" />

      <div className="container-limit py-14">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {/* Col 1: Brand */}
          <div>
            <div className="flex items-center gap-3">
              <img src="/trevia-logo.svg" alt="Trevia Group" className="h-11 w-auto brightness-0 invert" />
              <span className="font-display text-xl font-extrabold tracking-tight uppercase">Trevia Group</span>
            </div>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed">{t.footerTagline}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              <a href={WHATSAPP} target="_blank" rel="noreferrer" className="glass-dark inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-slate-300 transition hover:bg-white/10 hover:text-white">
                <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
              </a>
              <button onClick={() => setShowWechat(true)} className="glass-dark inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-slate-300 transition hover:bg-white/10 hover:text-white">
                <ImageIcon className="h-3.5 w-3.5" /> {t.wechat}
              </button>
              <button onClick={() => setShowCard(true)} className="glass-dark inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-slate-300 transition hover:bg-white/10 hover:text-white">
                <ImageIcon className="h-3.5 w-3.5" /> {t.businessCard}
              </button>
            </div>
          </div>

          {/* Col 2: Navigation */}
          <div>
            <div className="mb-4 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{t.footerNav}</div>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/" className="text-slate-400 transition hover:text-white">{t.nav.home}</Link></li>
              <li><Link to="/about" className="text-slate-400 transition hover:text-white">{t.nav.about}</Link></li>
              <li><Link to="/freight" className="text-slate-400 transition hover:text-white">{t.nav.freight}</Link></li>
              <li><Link to="/sourcing" className="text-slate-400 transition hover:text-white">{t.nav.sourcing}</Link></li>
              <li><Link to="/catalog" className="text-slate-400 transition hover:text-white">{t.nav.catalog}</Link></li>
              <li><Link to="/blog" className="text-slate-400 transition hover:text-white">{t.nav.blog}</Link></li>
              <li><Link to="/contact" className="text-slate-400 transition hover:text-white">{t.nav.contact}</Link></li>
            </ul>
          </div>

          {/* Col 3: Company */}
          <div>
            <div className="mb-4 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{t.footerCompany}</div>
            <ul className="space-y-2 text-sm text-slate-400">
              <li className="font-medium text-white">Trevia Group OÜ</li>
              <li>Harju maakond, Tallinn, Kesklinna linnaosa</li>
              <li>Narva mnt 5, 10117</li>
              <li className="pt-1 text-xs">VAT: EE102872110</li>
              <li className="text-xs">Reg. No: 17257329</li>
            </ul>
          </div>

          {/* Col 4: Contact */}
          <div>
            <div className="mb-4 text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{t.footerContact}</div>
            <ul className="space-y-3 text-sm">
              <li>
                <a className="inline-flex items-center gap-2.5 text-slate-400 transition hover:text-white" href={`mailto:${MAIL_TO}`}>
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal/10">
                    <Mail className="h-3.5 w-3.5 text-teal" />
                  </div>
                  {MAIL_TO}
                </a>
              </li>
              <li>
                <a className="inline-flex items-center gap-2.5 text-slate-400 transition hover:text-white" href={WHATSAPP} target="_blank" rel="noreferrer">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal/10">
                    <Phone className="h-3.5 w-3.5 text-teal" />
                  </div>
                  +386 31 708 897
                </a>
              </li>
            </ul>
            <p className="mt-5 text-xs text-slate-500">{t.replySLA}</p>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5">
        <div className="container-limit flex flex-col items-center justify-between gap-2 py-5 text-xs text-slate-500 sm:flex-row">
          <span>&copy; {new Date().getFullYear()} Trevia Group OÜ. {t.footerCopyright}</span>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-white transition">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition">Terms of Service</Link>
            <span>Tallinn, Estonia</span>
          </div>
        </div>
      </div>

      <Modal open={showWechat} onClose={() => setShowWechat(false)} title={t.wechat} imgSrc="/wechat-qr.png" />
      <Modal open={showCard} onClose={() => setShowCard(false)} title={t.businessCard} imgSrc="/business-card.png" />
    </footer>
  );
}
